import {useEffect, useState, useMemo } from "react";
import CustomDataGrid from "../../../components/dms_common/CustomDataGrid";
import { getTotalOrderValueColumns} from "../utils/utils";
import {
  setClaimSummaryRows,
  setTotalOrderValueRows,
  setSelectedClaimSubType,
  setSelectedClaimType,
  setTotalOrderValueCheckedClaimIds,
  setMonth,
  setSearchText
} from "../../../stateManagement/actions/claims";
import { useSelector, useDispatch } from "react-redux";
import MiniClaimSummary from "../components/MiniClaimSummary";
import { Loader } from "../../../components/loader/Loader";
import { getFormattedDateRange, getCurrentDateTime, getLast12Months} from "../../../utils/calenderUtils";
import { getTovInvoices, getClaims } from "../../../services/claimService";
import { formatDecimal } from "../../../components/dms_common/DmsUtils";
import "./PurchaseReturns.css";
import { logLoadTrace } from "../../../../src/firebase";

const TotalOrderValue: React.FC = () => {
  const { totalOrderValueRows, totalOrderValueCheckedClaimIds, month, searchText} = useSelector(
    (state: any) => state.claims
  );
  const loginId = JSON.parse(localStorage.authContext)?.user?.loginId;
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(false);

  const filteredRows = useMemo(() => {
    const term = searchText.toLowerCase().replace(/\s/g, '');
    if (!term) return totalOrderValueRows;

    return totalOrderValueRows.filter((row: any) => {
      const matches = [row.invoiceNo, row.outletNameCode].some(field =>
        field?.toLowerCase().replace(/\s/g, '').includes(term));
        return matches;
  });
  }, [searchText, totalOrderValueRows]);

  const visibleCheckedIds = useMemo(() => {
    return totalOrderValueCheckedClaimIds.filter((id: any) =>
      filteredRows.some((row: any) => row.id === id)
    );
  }, [totalOrderValueCheckedClaimIds, filteredRows]);

    const fetchData = async () => {
      if (!month) return;
      setLoader(true);
      try {
        const [formattedStartDate, formattedEndDate] = getFormattedDateRange(month);
        if (!formattedStartDate || !formattedEndDate) return;
  
        const [tovInvoicesResponse, claimsResponse] = await Promise.all([
          getTovInvoices(
            formattedStartDate.replace("%", " "), 
            formattedEndDate.replace("%", " ")
          ),
          getClaims(
            formattedStartDate.replace("%", "T"),
            getCurrentDateTime(new Date(), true).replace("%", "T"),
            "TotalOrderValue"
          )
        ]);
  
        const tovInvoicesData = tovInvoicesResponse || [];
        const claimsData = claimsResponse || [];
  
        const claimedInvoiceMap: Record<string, { status: string, approvedAmount: number }> = {};
        claimsData.forEach((claim: any) => {
          claim.claimDetails.forEach((detail: any) => {
            if (detail.referenceNumber) {
              if (claim.status === 'APPROVED' || 
                  claim.status === 'REQUESTED' ||
                  (claim.status === 'PARTIALLY_APPROVED' && detail.approvedAmount > 0)) {
                claimedInvoiceMap[detail.referenceNumber] = {
                  status: claim.status,
                  approvedAmount: detail.approvedAmount
                };
              }
            }
          });
        });
        
        const unclaimedInvoices = tovInvoicesData.filter((invoice: any) => 
          !claimedInvoiceMap[invoice.invoice_number] || 
          (claimedInvoiceMap[invoice.invoice_number].status !== 'APPROVED' &&
           claimedInvoiceMap[invoice.invoice_number].status !== 'REQUESTED' &&
           !(claimedInvoiceMap[invoice.invoice_number].status === 'PARTIALLY_APPROVED' && 
             claimedInvoiceMap[invoice.invoice_number].approvedAmount > 0))
        );
  
        const rows = unclaimedInvoices.map((invoice: any, index: number) => ({
          id: `${invoice.invoice_number}-${index}`,
          invoiceNo: invoice.invoice_number,
          invoiceDateTime: invoice.creation_time,
          outletNameCode: `${invoice.outletName || "N/A"} - ${invoice.outletcode || "N/A"}`,
          cases: invoice.totalCaseQuantity,
          pieces: invoice.totalPieceQuantity,
          others: invoice.totalOtherUnitQuantity,
          totalAmount: invoice.total_amount,
          orderNo: `${invoice.order_number}-${loginId}`,
          discount: parseFloat(formatDecimal(Number(invoice.discount_info?.discount || invoice.discount_info?.finalBenefit))) || 0,
          showEyeIcon: true,
        }));
        dispatch(setTotalOrderValueRows(rows || []));
        
        const previousCheckedIds = totalOrderValueCheckedClaimIds;
        const filteredCheckedIds = previousCheckedIds.filter((id: any) =>
          rows.some(row => row.id === id)
        );
        dispatch(setTotalOrderValueCheckedClaimIds(
          filteredCheckedIds.length ? filteredCheckedIds :
            rows.filter(row => row.discount !== 0).map(row => row.id)
        ));
  
      } catch (error) {
        console.error("Error fetching TOV invoices:", error);
      } finally {
        setLoader(false);
      }
    };

  const init = async () => {
    setLoader(true);
    await Promise.allSettled([fetchData()]).finally(
      () => {
        setLoader(false);
      }
    )
  }

  useEffect(() => {
    if (month) {
    logLoadTrace(init, "claims_tov_secondary_schemes_page_load_time");
    }
  },[month]);

  useEffect(() => {
    const updatedClaimSummaryRows = totalOrderValueRows?.filter((obj: any) =>
      totalOrderValueCheckedClaimIds.includes(obj.id)
    );
    dispatch(setClaimSummaryRows(updatedClaimSummaryRows));
  }, [totalOrderValueCheckedClaimIds, totalOrderValueRows]);

  useEffect(() => {
    dispatch(setSelectedClaimType("Secondary Schemes"));
    dispatch(setSelectedClaimSubType("Total Order Value"));
  }, []);

  const handleSetCheckedIdArray = (ids: any[]) => {
    const validIds = ids.filter((id) => {
      const row = totalOrderValueRows.find((row: any) => row.id === id);
      return row && row.discount !== 0; 
    });
    dispatch(setTotalOrderValueCheckedClaimIds(validIds));
  };

  useEffect(() => {
    return () => {
      if (!window.location.pathname.includes('totalOrderValue')) {
        dispatch(setSearchText(""));
        dispatch(setTotalOrderValueCheckedClaimIds([]));
        const currentMonth = getLast12Months()[0];
        dispatch(setMonth(currentMonth)); 
        dispatch(setSelectedClaimSubType(""));
      }
    };
  }, []); 
 
  return (
    <div className="claims_table_container">
      {loader ? (
        <Loader/>
      ) : (
        <>
          <MiniClaimSummary rows={totalOrderValueRows} />
          <CustomDataGrid
            columnsMetaData={getTotalOrderValueColumns()}
            rows={filteredRows}
            isCheckboxSelection={true}
            insideSecondarySchemes={true}
            redirectUrl="/claims/create/totalOrderValue/invoiceDetails/"
            checkedIdArray={visibleCheckedIds}
            setCheckedIdArray={handleSetCheckedIdArray}
          />
        </>
      )}
    </div>
  );
};
export default TotalOrderValue;
